import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ProfileCard from './ProfileCard';
import RecentOrderCard from './RecentOrderCard';
import BillCard from './BillCard';
import EmptyState from './EmptyState';
import { 
  Package, 
  MapPin, 
  FileText, 
  ShoppingCart, 
  Headphones, 
  ChevronRight, 
  ArrowRight 
} from 'lucide-react';

const DashboardTab = ({ 
  dashboardData, 
  addresses = [], 
  loading, 
  setActiveTab, 
  onViewOrderDetails, 
  onViewBill 
}) => {
  const { profile = {}, recentOrders = [], recentBills = [] } = dashboardData;
  const navigate = useNavigate();

  // Select cart items count
  const cartItems = useSelector((state) => state.cart.cartItems);
  const cartCount = cartItems ? cartItems.length : 0;

  // Handles Quick Action Clicks
  const quickActions = [
    { 
      id: 'orders', 
      label: 'My Orders', 
      desc: 'Track all your orders', 
      icon: Package, 
      onClick: () => setActiveTab('orders') 
    },
    { 
      id: 'cart', 
      label: 'My Cart', 
      desc: 'Continue your purchase', 
      icon: ShoppingCart, 
      onClick: () => navigate('/cart') 
    },
    { 
      id: 'bills', 
      label: 'Invoices', 
      desc: 'View uploaded invoices', 
      icon: FileText, 
      onClick: () => setActiveTab('bills') 
    },
    { 
      id: 'support', 
      label: 'Contact Support', 
      desc: 'Get help with orders, billing & more', 
      icon: Headphones, 
      onClick: () => window.location.href = 'mailto:support@regardtrader.in?subject=REGARD TRADERS Support Request' 
    }
  ];

  // Find default delivery address
  const defaultAddress = addresses ? (addresses.find(addr => addr.isDefault) || (addresses.length > 0 ? addresses[0] : null)) : null;

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Loading Skeletons */}
        <div className="dashboard-header-block">
          <div className="skeleton profile-summary-card" style={{ height: '180px', width: '100%' }} />
        </div>
        
        <div className="quick-actions-section">
          <div className="skeleton" style={{ height: '24px', width: '150px', marginBottom: '10px' }} />
          <div className="quick-actions-grid">
            <div className="skeleton quick-action-card" style={{ height: '120px' }} />
            <div className="skeleton quick-action-card" style={{ height: '120px' }} />
            <div className="skeleton quick-action-card" style={{ height: '120px' }} />
            <div className="skeleton quick-action-card" style={{ height: '120px' }} />
          </div>
        </div>

        <div className="default-address-section">
          <div className="skeleton" style={{ height: '24px', width: '200px', marginBottom: '10px' }} />
          <div className="skeleton default-address-card" style={{ height: '120px', width: '100%' }} />
        </div>

        <div className="dashboard-bottom-grid">
          <div className="skeleton" style={{ height: '350px' }} />
          <div className="skeleton" style={{ height: '350px' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Profile Card Section */}
      <div className="dashboard-header-block">
        <ProfileCard profile={profile} onAvatarClick={() => setActiveTab('profile')} />
      </div>

      {/* Quick Actions Panel */}
      <div className="quick-actions-section">
        <div className="section-title-row">
          <h3>Quick Actions</h3>
        </div>
        <div className="quick-actions-grid">
          {quickActions.map(action => {
            const Icon = action.icon;
            return (
              <div 
                key={action.id} 
                className="quick-action-card"
                onClick={action.onClick}
              >
                <div className="action-icon-circle-wrapper">
                  <div className="action-icon-circle">
                    <Icon size={20} />
                  </div>
                  {action.id === 'cart' && cartCount > 0 && (
                    <span className="quick-action-badge">{cartCount}</span>
                  )}
                </div>
                <h4>
                  {action.label}
                  <span className="mobile-arrow"> →</span>
                </h4>
                <p>
                  {action.desc}
                  <span className="desktop-arrow"> →</span>
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Default Delivery Address Section */}
      <div className="default-address-section">
        <h3>Default Delivery Address</h3>
        {defaultAddress ? (
          <div className="default-address-card">
            <span className="address-default-badge">Default</span>
            <div className="address-left-section">
              <div className="address-icon-box">
                <MapPin size={22} />
              </div>
              <div className="address-info-box">
                <h4>{defaultAddress.shopName}</h4>
                <div className="address-meta-line">
                  {defaultAddress.contactPerson} • {defaultAddress.phone}
                </div>
                <div className="address-text-line">
                  {defaultAddress.addressLine1}
                  {defaultAddress.addressLine2 && `, ${defaultAddress.addressLine2}`}
                </div>
                <div className="address-text-line">
                  {defaultAddress.city}, {defaultAddress.state} - {defaultAddress.pinCode}
                </div>
                <div className="address-text-line">India</div>
              </div>
            </div>
            <button onClick={() => setActiveTab('addresses')} className="btn-manage-addresses">
              Manage Addresses <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <EmptyState 
            icon="📍" 
            title="No Address Added Yet" 
            description="Add your delivery address to speed up ordering."
            actionText="Add Address"
            onActionClick={() => setActiveTab('addresses')}
          />
        )}
      </div>

      {/* Bottom Grid: Recent Orders & Recent Bills */}
      <div className="dashboard-bottom-grid">
        
        {/* Recent Orders Pane */}
        <div className="dashboard-pane">
          <div className="pane-title-row">
            <h3>Recent Orders</h3>
            <button onClick={() => setActiveTab('orders')} className="section-title-link">
              <span className="view-all-text-desktop">View All Orders</span>
              <span className="view-all-text-mobile">View All</span>
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="pane-list">
            <div className="recent-orders-header-row">
              <span>Order Details</span>
              <span>Status</span>
              <span>Amount</span>
              <span>Action</span>
            </div>
            {recentOrders.length === 0 ? (
              <EmptyState 
                icon="📦" 
                title="No Orders Yet" 
                description="Your orders will appear here once you place them."
                actionText="Go to Shop"
                onActionClick={() => window.location.href = '/shop'}
              />
            ) : (
              recentOrders.map(order => (
                <RecentOrderCard 
                  key={order._id} 
                  order={order} 
                  onViewDetails={onViewOrderDetails} 
                />
              ))
            )}
          </div>
        </div>

        {/* Recent Bills Pane */}
        <div className="dashboard-pane">
          <div className="pane-title-row">
            <h3>Recent Bills</h3>
            <button onClick={() => setActiveTab('bills')} className="section-title-link">
              <span className="view-all-text-desktop">View All Bills</span>
              <span className="view-all-text-mobile">View All</span>
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="pane-list">
            {recentBills.length === 0 ? (
              <EmptyState 
                icon="📄" 
                title="No Bills Available" 
                description="Uploaded invoices will appear here."
              />
            ) : (
              recentBills.map(bill => (
                <BillCard 
                  key={bill._id} 
                  bill={bill} 
                  onViewBill={onViewBill} 
                />
              ))
            )}
          </div>
        </div>

      </div>

    </div>
  );
};

export default DashboardTab;
